'use client';

import {useContext, useEffect, useRef} from 'react';
import styled from '@emotion/styled';

import {Label, FieldComponent, inputStyle} from 'Input';
import {SelectContext} from 'Select/Select';

import type {CSSProperties} from 'react';
import type {InputInputComponent, InputStyles} from 'Input';
import type {SelectProps} from 'Select/types';

const Container = styled.div<Pick<SelectProps, 'isOpen'>>`
  position: relative;
  flex-grow: 1;
  height: 100%;

  input:read-only {
    cursor: default;
  }

  input {
    ${props => ({display: props.isOpen ? undefined : 'none'})}
  }

  input + div {
    ${props => ({display: props.isOpen ? 'none' : ''})}
  }
`;

const divStyle = (style: InputStyles, acc: CSSProperties | undefined) => {
  return inputStyle(style, {
    paddingTop: style.isSmall ? '4px' : '19px',
    ...acc,
  });
};

export const WrapInput: InputInputComponent = props => {
  const divProps = {
    ...props.inputProps,

    children: props.value,
    onFocus: props.onFocus,

    autoComplete: 'off',
    onBlur: undefined,
    tabIndex: 0,

    style: divStyle(props.style, props.inputProps?.style),

    'data-qa': `${props.qa}-input-div`,
  };

  return (
    <Container>
      <Label {...props} />
      <FieldComponent as="div" {...divProps} />
    </Container>
  );
};

export const WrapInputEditable: InputInputComponent = props => {
  const {isOpen} = useContext(SelectContext);

  const inputRef = useRef<HTMLInputElement>(null);

  const inputProps = {
    ...props.inputProps,

    autoComplete: 'off',
    onFocus: undefined,
    style: inputStyle(props.style, props.inputProps?.style),

    'data-qa': `${props.qa}-input`,
  };

  const divProps = {
    ...props.inputProps,

    children: props.value,
    onFocus: props.onFocus,

    autoComplete: 'off',
    onBlur: undefined,
    tabIndex: isOpen ? undefined : 0,
    style: divStyle(props.style, props.inputProps?.style),

    'data-qa': `${props.qa}-input-div`,
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTimeout(() => {
      inputRef.current?.focus();
    });
  }, [isOpen]);

  return (
    <Container isOpen={isOpen}>
      <Label {...props} />
      <FieldComponent ref={inputRef} {...inputProps} />
      <FieldComponent as="div" {...divProps} />
    </Container>
  );
};
